// Pushes records that were created while offline up to the PHP API as
// soon as connectivity returns. Collections are flushed before songs
// (songs reference a collection_id), and each successful insert
// clears the pendingSync flag in IndexedDB.

import { post, put, ApiError } from '../api'
import { OFFLINE_ENABLED } from '../../config/offline'
import {
  listPendingCollections,
  listPendingSongs,
  clearCollectionPendingSync,
  clearSongPendingSync,
} from './songCache'

export interface SyncResult {
  collectionsSynced: number
  songsSynced: number
  failed: number
}

let inFlight: Promise<SyncResult> | null = null

export function syncPending(): Promise<SyncResult> {
  if (!OFFLINE_ENABLED) {
    return Promise.resolve({ collectionsSynced: 0, songsSynced: 0, failed: 0 })
  }
  if (inFlight) return inFlight
  inFlight = runSync().finally(() => { inFlight = null })
  return inFlight
}

async function runSync(): Promise<SyncResult> {
  const result: SyncResult = { collectionsSynced: 0, songsSynced: 0, failed: 0 }

  // 1. Collections first — songs may reference one of these IDs.
  const pendingCollections = await listPendingCollections()
  for (const rec of pendingCollections) {
    const c = rec.collection
    try {
      await post('/collections', {
        id:          c.id,
        user_id:     c.userId,
        name:        c.name,
        artist:      c.artist,
        share_token: c.shareToken,
      })
      await clearCollectionPendingSync(c.id)
      result.collectionsSynced++
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Already exists upstream — mark as synced.
        await clearCollectionPendingSync(c.id)
        result.collectionsSynced++
      } else {
        console.warn('[sync] collection insert failed:', err)
        result.failed++
      }
    }
  }

  // 2. Songs.
  const pendingSongs = await listPendingSongs()
  for (const rec of pendingSongs) {
    const s = rec.song
    try {
      await post('/songs', {
        id:            s.id,
        user_id:       s.userId,
        collection_id: s.collectionId,
        title:         s.title,
        notes:         s.notes ?? '',
        info:          s.info ?? '',
        listen_url:    s.listenUrl ?? '',
        share_token:   s.shareToken,
        attachments:   s.attachments ?? [],
        elements:      s.elements ?? [],
      })
      await clearSongPendingSync(s.id)
      result.songsSynced++
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Row already exists — update with local changes.
        try {
          await put(`/songs/${s.id}`, {
            title:    s.title,
            notes:    s.notes ?? '',
            info:     s.info ?? '',
            listen_url:  s.listenUrl ?? '',
            attachments: s.attachments ?? [],
            elements:    s.elements ?? [],
          })
          await clearSongPendingSync(s.id)
          result.songsSynced++
        } catch (updErr) {
          console.warn('[sync] song update fallback failed:', updErr)
          result.failed++
        }
      } else {
        console.warn('[sync] song insert failed:', err)
        result.failed++
      }
    }
  }

  return result
}

export async function getPendingCount(): Promise<number> {
  if (!OFFLINE_ENABLED) return 0
  const [c, s] = await Promise.all([listPendingCollections(), listPendingSongs()])
  return c.length + s.length
}
